import React from 'react';
import { compose } from 'redux';
import classnames from 'classnames';
import { withTranslation, WithTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-types-papi';
import OnScrollBottom, {
  DISTANCE_UNIT,
} from '@wix/da-shared-react/pkg/OnScrollBottom';
import LoadingIndicator from '@wix/da-shared-react/pkg/LoadingIndicator';
import withSectionFetchSpinner from '../../../../decorators/withSectionFetchSpinner';
import { PostsSortOrder } from '../../../../../types/posts';
import { USER_PROFILE_POSTS_SUB_PATHS } from '@wix/da-url';
import WidgetHeader from '../../../UserWidgets/_partials/WidgetHeader';
import EmptyState from '../_partials/EmptyState';
import PostsMobileList from '../_partials/PostsMobileList';
import JournalsGrid from '../_partials/PostsGrid';
import SortOrderSwitcher from '../_partials/SortOrderSwitcher';

import JournalsList from './JournalsList';
import FeaturedJournals from './FeaturedList';

import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import { withMobileContext } from '@wix/da-shared-react/pkg/Context/MobileContext';
import { SectionViewType } from '../../../../../types/sections';

import s from './Journals.scss';

export interface Props {
  streamId: string;
  viewType: SectionViewType;
  journals: PapiDeviation[];
  featuredJournals: PapiDeviation[];
  hasMoreJournals: boolean;
  hasMoreFeaturedJournals: boolean;
  isFetchingJournals: boolean;
  isFetchingFeaturedJournals: boolean;
  fetchMoreJournals: () => void;
  fetchMoreFeaturedJournals: () => void;
  sortOrder: PostsSortOrder;
  onSortOrderChange: (
    path: USER_PROFILE_POSTS_SUB_PATHS,
    sortOrder: PostsSortOrder
  ) => void;
}

const enhance = compose(
  withSectionFetchSpinner,
  withMobileContext,
  withTranslation()
);
export class Journals extends React.Component<
  Props & WithTranslation & { isMobile: boolean }
> {
  render() {
    const {
      streamId,
      viewType,
      journals,
      featuredJournals,
      hasMoreJournals,
      hasMoreFeaturedJournals,
      isFetchingJournals,
      isFetchingFeaturedJournals,
      fetchMoreJournals,
      fetchMoreFeaturedJournals,
      sortOrder,
      isMobile,
      t,
    } = this.props;

    const JournalsComponent =
      viewType === SectionViewType.LIST ? JournalsList : JournalsGrid;

    if (!journals.length && !featuredJournals.length) {
      return (
        <EmptyState
          className={s['empty-state']}
          postType="journal"
          view="large"
          CTAButtonModalType={ModalType.JOURNAL}
        />
      );
    }

    return (
      <div className={s['root']}>
        <ErrorBoundary>
          <div
            className={classnames(
              s['content'],
              journals.length === 0 && s['empty']
            )}
          >
            <OnScrollBottom
              distanceToBottom={2}
              distanceUnit={DISTANCE_UNIT.WINDOW_HEIGHT}
              onScrolledToBottom={
                hasMoreJournals && !isFetchingJournals
                  ? fetchMoreJournals
                  : undefined
              }
            >
              <div className={s['list']}>
                {isMobile ? (
                  journals.length === 0 ? (
                    <EmptyState
                      postType="journal"
                      view="large"
                      CTAButtonModalType={ModalType.JOURNAL}
                    />
                  ) : (
                    <PostsMobileList streamId={streamId} items={journals} />
                  )
                ) : (
                  <>
                    <WidgetHeader
                      extras={
                        <SortOrderSwitcher
                          className={s['sort-order-switch']}
                          value={sortOrder}
                          onChange={this.updateSortOrder}
                        />
                      }
                    />
                    <JournalsComponent streamId={streamId} items={journals} />
                  </>
                )}
                {isFetchingJournals && <LoadingIndicator />}
              </div>
            </OnScrollBottom>
          </div>
          {!isMobile && viewType === SectionViewType.LIST && (
            <div
              className={classnames(
                s['sidebar'],
                featuredJournals.length === 0 && s['empty']
              )}
            >
              <WidgetHeader
                header={t!('common.featuredJournals')}
                className={s['sidebar-header']}
              />
              <OnScrollBottom
                distanceToBottom={600}
                distanceUnit={DISTANCE_UNIT.PX}
                onScrolledToBottom={
                  hasMoreFeaturedJournals && !isFetchingFeaturedJournals
                    ? fetchMoreFeaturedJournals
                    : undefined
                }
              >
                <div className={s['list']}>
                  <FeaturedJournals
                    streamId={streamId}
                    items={featuredJournals}
                  />
                </div>
              </OnScrollBottom>
              {isFetchingFeaturedJournals && <LoadingIndicator />}
            </div>
          )}
        </ErrorBoundary>
      </div>
    );
  }

  updateSortOrder = sortOrder => {
    const { onSortOrderChange } = this.props;
    onSortOrderChange &&
      onSortOrderChange(USER_PROFILE_POSTS_SUB_PATHS.JOURNALS, sortOrder);
  };
}

export default enhance(Journals) as React.ComponentType<Props>;
